/**
 * Login Page - Olympus Betreuungsverwaltung
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogIn, Eye, EyeOff, AlertCircle, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/api/authAPI';
import OlympusIcon from '@/components/OlympusIcon';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [showPasswort, setShowPasswort] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // 2FA State
  const [needs2FA, setNeeds2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [tempToken, setTempToken] = useState('');

  const from = location.state?.from?.pathname || '/';

  // OAuth-Consent-Rueckkehr (fix(oauth): complete ChatGPT consent flow for
  // scan review MCP, 10.09.2026): die Consent-Seite (/oauth/authorize) linkt
  // nicht eingeloggte Nutzer auf /login?return_to=<aktueller Pfad>. Nur ein
  // relativer Pfad wird akzeptiert — kein '//' (protokoll-relativer Open-
  // Redirect), keine absolute fremde URL. Volle Seitennavigation (nicht
  // React-Router-navigate), damit die Consent-Seite ihren Serverzustand
  // (JWT-Cookie/Header) neu liest.
  const returnToParam = new URLSearchParams(location.search).get('return_to');
  const returnTo = (returnToParam && returnToParam.startsWith('/') && !returnToParam.startsWith('//'))
    ? returnToParam
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, passwort);
      if (result?.requires_2fa) {
        setNeeds2FA(true);
        setTempToken(result.temp_token);
        setLoading(false);
        return;
      }
      if (returnTo) {
        window.location.href = returnTo;
        return;
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Same-Origin-Helper (P0-Fix 26.09.2026): siehe authAPI.js SAME_ORIGIN_BASE
      // -- 2FA muss ueber jeden Zugriffsweg (Domain/VPN) funktionieren.
      const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');
      const res = await fetch(`${API}/api/auth/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: tempToken, totp_code: totpCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '2FA fehlgeschlagen');

      // Derselbe Token-Persistenzpfad wie der reguläre Login (authAPI.login()
      // ruft intern ebenfalls setAuth() auf) — vorher wurden hier direkt die
      // falschen localStorage-Keys 'token'/'benutzer' gesetzt, die authAPI/
      // AuthContext nie lasen (olympus_token/olympus_user), wodurch die Seite
      // nach dem Reload als nicht eingeloggt galt und sofort zurück auf
      // /login sprang.
      authAPI.setAuth(data.access_token, data.benutzer);
      window.location.href = returnTo || from;
    } catch (err) {
      setError(err.message || '2FA-Verifizierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="mx-auto mb-4">
            <OlympusIcon size={80} className="mx-auto" withBg />
          </div>
          <h1 className="text-3xl font-bold text-white">Olympus</h1>
          <p className="text-slate-400 mt-2">Betreuungsverwaltung</p>
        </div>

        {/* Login Card */}
        <Card className="border-slate-700 bg-slate-800/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white">
              {needs2FA ? 'Zwei-Faktor-Authentifizierung' : 'Anmelden'}
            </CardTitle>
            <CardDescription className="text-slate-400">
              {needs2FA 
                ? 'Geben Sie den Code aus Ihrer Authenticator-App ein'
                : 'Melden Sie sich mit Ihren Zugangsdaten an'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {needs2FA ? (
              <form onSubmit={handle2FA} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}
                <div className="flex justify-center mb-2">
                  <ShieldCheck className="w-12 h-12 text-blue-400" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="totp" className="text-slate-300">6-stelliger Code</Label>
                  <Input
                    id="totp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    required
                    autoFocus
                    className="bg-slate-700/50 border-slate-600 text-white text-center text-2xl tracking-[0.5em] placeholder:text-slate-500"
                    data-testid="login-2fa-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading || totpCode.length !== 6} data-testid="login-2fa-submit">
                  {loading ? 'Prüfe...' : 'Bestätigen'}
                </Button>
                <button type="button" onClick={() => { setNeeds2FA(false); setTotpCode(''); setError(''); }} className="w-full text-sm text-slate-500 hover:text-slate-300">
                  Zurück zum Login
                </button>
              </form>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@beispiel.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="passwort" className="text-slate-300">Passwort</Label>
                <div className="relative">
                  <Input
                    id="passwort"
                    type={showPasswort ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={passwort}
                    onChange={(e) => setPasswort(e.target.value)}
                    required
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswort(!showPasswort)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                  >
                    {showPasswort ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Anmelden...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="w-4 h-4" />
                    Anmelden
                  </span>
                )}
              </Button>
            </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
